import * as React from 'react';
import clsx from 'clsx';
import { ContactList } from '@/features/contacts/components/ContactList';
import type { ContactListHandle } from '@/features/contacts/types';
import { useChatContext } from '@/components/layout/ChatLayoutContext';
import { useEchoContacts } from '@/features/contacts/hooks/useEchoContacts';
import type { EchoKey } from '@/utils/constants';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { buildEchoSlugMap } from '@/utils/echoUtils';
import { useGroupChatMessages } from '@/hooks/useGroupChatMessages';
import { useAuth } from '@/hooks/useAuth';
import type { Echo } from '@/types';
import type { GroupChatState } from '@/features/chat/components/ChatView';
import type { EchoInfoEntry } from '@/components/chat/MessageList';
import groupChatAvatar from '@/assets/images/icons/group_chat_avatar.svg';

// OPTIMIZATION: Lazy load the heavy ChatView component
const ChatView = React.lazy(() =>
  import('@/features/chat/components/ChatView').then((module) => ({ default: module.ChatView }))
);

/**
 * Thin wrapper that calls useGroupChatMessages and passes results into ChatView.
 * Must be a separate component so the hook is called unconditionally.
 */
const GroupChatContent: React.FC<{
  groupId: string;
  echoes: Record<string, Echo>;
  onOpenSidebar: () => void;
}> = ({ groupId, echoes, onOpenSidebar }) => {
  const { user } = useAuth();
  const isAudioEnabled = user?.audio_enabled ?? true;

  const chat = useGroupChatMessages({
    groupId,
    ttsProvider: 'elevenlabs',
    generateAudio: isAudioEnabled,
  });

  // Resolve the two echo IDs that belong to this group
  const groupEchoIds = React.useMemo<[string, string] | null>(() => {
    try {
      const stored = localStorage.getItem('decho_group_chats');
      if (!stored) return null;
      const groups = JSON.parse(stored) as Array<{ id: string; echoIds: [string, string] }>;
      const match = groups.find((grp) => grp.id === groupId);
      return match?.echoIds ?? null;
    } catch {
      return null;
    }
  }, [groupId]);

  // Build echoInfoMap from ONLY the group's echoes (not all contacts)
  const echoInfoMap = React.useMemo<Record<string, EchoInfoEntry>>(() => {
    const map: Record<string, EchoInfoEntry> = {};
    if (!groupEchoIds) return map;
    for (const id of groupEchoIds) {
      const echo = echoes[id];
      if (echo) {
        map[id] = {
          id,
          name: echo.name,
          ...(echo.imageUrl != null ? { avatarUrl: echo.imageUrl } : {}),
        };
      }
    }
    return map;
  }, [echoes, groupEchoIds]);

  const echo1 = groupEchoIds?.[0] ? echoInfoMap[groupEchoIds[0]] : undefined;
  const echo2 = groupEchoIds?.[1] ? echoInfoMap[groupEchoIds[1]] : undefined;
  const groupName = echo1 && echo2 ? `${echo1.name} & ${echo2.name}` : 'Group Chat';

  // Virtual Echo object for ChatHeader — use the same group avatar icon as the contact list
  const groupEcho: Echo = {
    id: groupId,
    name: groupName,
    category: 'Group Chat',
    domain: 'General',
    imageUrl: groupChatAvatar,
  };

  const groupChatState: GroupChatState = {
    messages: chat.messages,
    isLoading: chat.isLoading,
    error: chat.error,
    sendMessage: chat.sendMessage,
    sendAudioMessage: chat.sendAudioMessage,
    clearConversation: chat.clearConversation,
    hasMore: chat.hasMore,
    loadMore: chat.loadMore,
    isSending: chat.isSending,
    echoInfoMap,
  };

  return (
    <ChatView
      key={groupId}
      echo={groupEcho}
      echoKey={'' as EchoKey}
      onOpenSidebar={onOpenSidebar}
      groupChat={groupChatState}
    />
  );
};

/**
 * MessagesPage Component
 * Two-column layout for chat: left is contact list, right is chat view.
 * Handles both regular echo chats and group chats.
 */
export const MessagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { echoName: echoNameParam, groupId } = useParams<{ echoName: string; groupId: string }>();
  const { selectedEcho, setSelectedEcho, setIsMobileMenuOpen } = useChatContext();
  const { echoes } = useEchoContacts();

  const isGroupChat = !!groupId;

  // Memoized slug maps — handles duplicate echo names with numeric suffixes
  const { idToSlug, slugToId } = React.useMemo(() => buildEchoSlugMap(echoes), [echoes]);

  const [showMobileChat, setShowMobileChat] = React.useState(() => {
    if (isGroupChat) return true;
    const state = location.state as { openChat?: boolean } | null;
    return (!!state?.openChat && !!selectedEcho) || !!echoNameParam;
  });
  const currentEcho = echoes[selectedEcho];

  // Resolve URL slug → echo ID
  React.useEffect(() => {
    if (!echoNameParam) return;
    const id = slugToId[echoNameParam];
    if (id && id !== selectedEcho) {
      setSelectedEcho(id as EchoKey);
      setShowMobileChat(true);
    }
  }, [echoNameParam, slugToId, selectedEcho, setSelectedEcho]);

  // Show mobile chat when navigating to group
  React.useEffect(() => {
    if (isGroupChat) {
      setShowMobileChat(true);
    }
  }, [isGroupChat]);

  const contactListRef = React.useRef<ContactListHandle>(null);
  const scrollPosRef = React.useRef(0);
  const prevShowMobileChatRef = React.useRef(showMobileChat);

  React.useLayoutEffect(() => {
    const prev = prevShowMobileChatRef.current;
    if (!prev && showMobileChat) {
      scrollPosRef.current = contactListRef.current?.getScrollTop() ?? 0;
    } else if (prev && !showMobileChat) {
      contactListRef.current?.setScrollTop(scrollPosRef.current);
    }
    prevShowMobileChatRef.current = showMobileChat;
  }, [showMobileChat]);

  const handleEchoSelect = (echoKey: string) => {
    setSelectedEcho(echoKey as EchoKey);
    const slug = idToSlug[echoKey] ?? echoKey;
    navigate(`/messages/${slug}`);
    setShowMobileChat(true);
  };

  // Sync URL when selectedEcho exists but URL param is missing
  // (e.g., clicking "Echo Chats" in sidebar navigates to /messages without a slug)
  const hasRedirected = React.useRef(false);
  React.useEffect(() => {
    if (echoNameParam || isGroupChat || hasRedirected.current) return;
    if (!selectedEcho) return;
    const slug = idToSlug[selectedEcho];
    if (!slug) return;
    hasRedirected.current = true;
    navigate(`/messages/${slug}`, { replace: true });
  }, [echoNameParam, isGroupChat, selectedEcho, idToSlug, navigate]);

  // Detect back navigation: when URL loses the echo/group param, show contact list
  React.useEffect(() => {
    if (!echoNameParam && !isGroupChat) {
      setShowMobileChat(false);
    }
  }, [echoNameParam, isGroupChat]);

  const handleMobileBack = () => {
    window.history.back();
  };

  const touchStartX = React.useRef<number | null>(null);
  const isMultiTouch = React.useRef(false);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    const touches = e.targetTouches;
    if (!touches) return;
    if (touches.length > 1) {
      isMultiTouch.current = true;
      return;
    }
    isMultiTouch.current = false;
    const first = touches.item(0);
    if (first) {
      touchStartX.current = first.clientX;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.targetTouches && e.targetTouches.length > 1) {
      isMultiTouch.current = true;
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (isMultiTouch.current) {
      touchStartX.current = null;
      isMultiTouch.current = false;
      return;
    }
    const changed = e.changedTouches;
    const startX = touchStartX.current;
    if (startX == null || !changed) return;
    const firstEnd = changed.item(0);
    if (!firstEnd) return;
    const touchEndX = firstEnd.clientX;
    const distance = touchEndX - startX;

    if (distance > minSwipeDistance) {
      handleMobileBack();
    }

    touchStartX.current = null;
  };

  const handleOpenSidebar = () => {
    setIsMobileMenuOpen(true);
  };

  const handleResumeChat = () => {
    if (!showMobileChat && window.innerWidth < 1280 && currentEcho) {
      navigate(`/messages/${idToSlug[selectedEcho] ?? selectedEcho}`);
    }
    setShowMobileChat(true);
  };

  const renderChatContent = () => {
    if (isGroupChat && groupId) {
      return (
        <React.Suspense
          fallback={
            <div className='flex size-full items-center justify-center bg-gray-50'>
              <div className='size-8 animate-spin rounded-full border-4 border-brand-300 border-t-brand-800' />
            </div>
          }
        >
          <GroupChatContent
            key={groupId}
            groupId={groupId}
            echoes={echoes}
            onOpenSidebar={handleOpenSidebar}
          />
        </React.Suspense>
      );
    }

    if (currentEcho) {
      return (
        <React.Suspense
          fallback={
            <div className='flex size-full items-center justify-center bg-gray-50'>
              <div className='size-8 animate-spin rounded-full border-4 border-brand-300 border-t-brand-800' />
            </div>
          }
        >
          <ChatView
            key={selectedEcho}
            echo={currentEcho as Echo}
            echoKey={selectedEcho}
            onOpenSidebar={handleOpenSidebar}
          />
        </React.Suspense>
      );
    }

    return (
      <div className='flex h-full items-center justify-center bg-gray-50 text-gray-500'>
        Select a chat to start messaging
      </div>
    );
  };

  return (
    <div className='flex size-full overflow-hidden'>
      <div
        className={clsx(
          'h-full border-r border-gray-200 bg-brand-25 transition-all',
          !showMobileChat ? 'block w-full' : 'hidden',
          'xl:block xl:w-[450px] xl:shrink-0'
        )}
      >
        <ContactList
          ref={contactListRef}
          selectedEchoId={isGroupChat ? '' : selectedEcho}
          onEchoSelect={handleEchoSelect}
          onOpenSidebar={handleOpenSidebar}
          onClose={handleResumeChat}
        />
      </div>

      <div
        onTouchStart={showMobileChat ? onTouchStart : undefined}
        onTouchMove={showMobileChat ? onTouchMove : undefined}
        onTouchEnd={showMobileChat ? onTouchEnd : undefined}
        className={clsx(
          'h-full min-w-0 flex-col',
          showMobileChat ? 'flex w-full' : 'hidden',
          'xl:flex xl:flex-1'
        )}
      >
        {renderChatContent()}
      </div>
    </div>
  );
};

export default MessagesPage;
